/**
 * Tools API Client
 *
 * API functions for fetching, validating, and testing tools
 * Migrated from tool-browser.js API calls
 */

import { apiClient, baseApiClient } from './client';
import type {
  ToolsResponse,
  MCPToolsResponse,
  ToolValidationRequest,
  ToolValidationResponse,
} from '../../types/tools';

// Tool Testing Types
export interface ToolTestRequest {
  tool_name: string;
  parameters: Record<string, unknown>;
}

export interface ToolTestResponse {
  success: boolean;
  tool_name: string;
  result: unknown;
  error: string | null;
  error_type: string | null;
  execution_time_ms: number;
  parameters_used: Record<string, unknown>;
}

export interface ToolSchemaResponse {
  name: string;
  description: string;
  category: string | null;
  enabled: boolean;
  parameters: Record<string, unknown>;
  parameters_info: Record<string, {
    required: boolean;
    type?: string;
    default?: unknown;
  }>;
  is_async: boolean;
  requires_user_context: boolean;
}

// MCP Connection Status Types
export type MCPConnectionState = 'connected' | 'connecting' | 'reconnecting' | 'disconnected' | 'failed';

export interface MCPConnectionStatus {
  server_id: string;
  state: MCPConnectionState;
  health: string;
  retry_count: number;
  max_retries: number;
  next_retry_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  connected_at: string | null;
  uptime_seconds: number | null;
  server_type: string;
  transport: string;
  tool_count: number;
}

export interface MCPPoolStatus {
  total_servers: number;
  connected_count: number;
  reconnecting_count: number;
  failed_count: number;
  servers: MCPConnectionStatus[];
  pool_healthy: boolean;
  degraded: boolean;
}

/**
 * Fetch available custom and builtin tools
 * @param language - Language for display names ('cn' or 'en')
 */
export const fetchAvailableTools = async (
  language: 'cn' | 'en' = 'cn'
): Promise<ToolsResponse> => {
  const params = new URLSearchParams();
  params.append('language', language);

  const url = `/tools/available?${params.toString()}`;
  const response = await apiClient.get<ToolsResponse>(url);
  return response.data;
};

/**
 * Fetch available MCP servers and tools
 * @param forceReconnect - Force reconnect to MCP servers
 * @param language - Language for display names ('cn' or 'en')
 */
export const fetchAvailableMCPServers = async (
  forceReconnect = false,
  language: 'cn' | 'en' = 'cn'
): Promise<MCPToolsResponse> => {
  const params = new URLSearchParams();
  if (forceReconnect) {
    params.append('force_reconnect', 'true');
  }
  params.append('language', language);

  const url = `/tools/mcp/available?${params.toString()}`;
  const response = await apiClient.get<MCPToolsResponse>(url);
  return response.data;
};

/**
 * Validate tools configuration
 */
export const validateToolsConfig = async (
  config: ToolValidationRequest
): Promise<ToolValidationResponse> => {
  const response = await apiClient.post<ToolValidationResponse>(
    '/tools/validate',
    config
  );
  return response.data;
};

/**
 * Test a tool by invoking it with parameters
 */
export const testTool = async (
  request: ToolTestRequest
): Promise<ToolTestResponse> => {
  const response = await apiClient.post<ToolTestResponse>(
    '/tools/test',
    request
  );
  return response.data;
};

/**
 * Get the schema for a specific tool
 */
export const getToolSchema = async (
  toolName: string
): Promise<ToolSchemaResponse> => {
  const response = await apiClient.get<ToolSchemaResponse>(
    `/tools/schema/${encodeURIComponent(toolName)}`
  );
  return response.data;
};

/**
 * Fetch MCP connection pool status
 * Returns detailed connection state for all MCP servers
 * Uses baseApiClient because endpoint is at /v1/mcp/* not /v1/admin/mcp/*
 */
export const fetchMCPStatus = async (): Promise<MCPPoolStatus> => {
  const response = await baseApiClient.get<MCPPoolStatus>('/v1/mcp/servers/status');
  return response.data;
};

/**
 * Refresh MCP connections
 * Disconnects and reconnects all MCP servers
 * Uses baseApiClient because endpoint is at /v1/mcp/* not /v1/admin/mcp/*
 */
export const refreshMCPConnections = async (): Promise<{ message: string; total: number; connected: number }> => {
  const response = await baseApiClient.post<{ message: string; total: number; connected: number }>('/v1/mcp/refresh');
  return response.data;
};

// Tool Performance Types
export interface ToolPerfStats {
  call_count: number;
  error_count: number;
  avg_ms: number;
  max_ms: number;
  min_ms: number;
  error_rate: number;
  last_error: string | null;
}

export interface ToolPerfResponse {
  total_tools_tracked: number;
  total_calls: number;
  total_errors: number;
  slow_tools: string[];
  tools: Record<string, ToolPerfStats>;
}

/**
 * Fetch tool execution performance stats
 */
export const fetchToolPerformance = async (
  slowThresholdMs = 5000
): Promise<ToolPerfResponse> => {
  const response = await apiClient.get<ToolPerfResponse>(
    `/tools/perf?slow_threshold_ms=${slowThresholdMs}`
  );
  return response.data;
};

/**
 * Reset tool performance stats
 */
export const resetToolPerformance = async (): Promise<{ status: string }> => {
  const response = await apiClient.post<{ status: string }>('/tools/perf/reset');
  return response.data;
};

export const toolsApi = {
  fetchAvailableTools,
  fetchAvailableMCPServers,
  validateToolsConfig,
  testTool,
  getToolSchema,
  fetchMCPStatus,
  refreshMCPConnections,
  fetchToolPerformance,
  resetToolPerformance,
};
